import React, { useState, useEffect } from 'react';
import { internalHandoverApi } from '../../../api/internalHandoverApi';
import { projectApi } from '../../../api/projectApi';
import { adminApi } from '../../../api/adminApi';
import './InternalHandover.css';

export default function InternalHandover() {
    const [handovers, setHandovers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list'); // 'list' or 'form'
    const [selectedHandover, setSelectedHandover] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        absent_from: '',
        absent_until: '',
        general_representative_id: '',
        items: []
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [hData, pData, uData] = await Promise.all([
                internalHandoverApi.getHandovers(),
                projectApi.getProjects(),
                adminApi.getUsers()
            ]);
            setHandovers(hData);
            setProjects(pData);
            setUsers(uData);
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setFormData({
            absent_from: '',
            absent_until: '',
            general_representative_id: '',
            items: [{
                project_id: '',
                title: '',
                item_number: '',
                notes: '',
                internal_link: '',
                item_representative_id: '',
                files: []
            }]
        });
        setSelectedHandover(null);
        setView('form');
    };

    const handleEdit = async (handover) => {
        setLoading(true);
        try {
            const details = await internalHandoverApi.getHandoverDetails(handover.id);
            setFormData({
                absent_from: details.absent_from ? details.absent_from.split('T')[0] : '',
                absent_until: details.absent_until ? details.absent_until.split('T')[0] : '',
                general_representative_id: details.general_representative_id || '',
                items: details.items.map(item => ({
                    ...item,
                    project_id: item.project_id || '',
                    item_representative_id: item.item_representative_id || ''
                }))
            });
            setSelectedHandover(handover);
            setView('form');
        } catch (err) {
            console.error('Error fetching details:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddItem = () => {
        setFormData({
            ...formData,
            items: [...formData.items, {
                project_id: '',
                title: '',
                item_number: '',
                notes: '',
                internal_link: '',
                item_representative_id: '',
                files: []
            }]
        });
    };

    const handleRemoveItem = (index) => {
        const newItems = [...formData.items];
        newItems.splice(index, 1);
        setFormData({ ...formData, items: newItems });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        setFormData({ ...formData, items: newItems });
    };

    const handleSave = async () => {
        try {
            if (selectedHandover) {
                await internalHandoverApi.updateHandover(selectedHandover.id, formData);
            } else {
                await internalHandoverApi.createHandover(formData);
            }
            setView('list');
            fetchData();
        } catch (err) {
            alert('Fehler beim Speichern');
        }
    };

    if (view === 'form') {
        return (
            <div className="internal-handover-container">
                <div className="internal-handover-header">
                    <h2>{selectedHandover ? 'Übergabe bearbeiten' : 'Neue Übergabe erstellen'}</h2>
                    <div className="header-actions">
                        <button className="btn-secondary" onClick={() => setView('list')}>Abbrechen</button>
                        <button className="btn-primary" onClick={handleSave}>Speichern</button>
                    </div>
                </div>

                <div className="handover-form-card">
                    <div className="form-section">
                        <h3>Allgemeine Infos</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Abwesend von</label>
                                <input 
                                    type="date" 
                                    value={formData.absent_from} 
                                    onChange={e => setFormData({...formData, absent_from: e.target.value})} 
                                />
                            </div>
                            <div className="form-group">
                                <label>Abwesend bis</label>
                                <input 
                                    type="date" 
                                    value={formData.absent_until} 
                                    onChange={e => setFormData({...formData, absent_until: e.target.value})} 
                                />
                            </div>
                            <div className="form-group">
                                <label>Haupt-Vertretung</label>
                                <select 
                                    value={formData.general_representative_id} 
                                    onChange={e => setFormData({...formData, general_representative_id: e.target.value})}
                                >
                                    <option value="">Wähle eine Person...</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>{u.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3>Übergabe-Punkte</h3>
                            <button className="btn-small" onClick={handleAddItem}>+ Punkt hinzufügen</button>
                        </div>

                        {formData.items.map((item, idx) => (
                            <div key={idx} className="handover-item-box">
                                <div className="item-header">
                                    <h4>Punkt #{idx + 1}</h4>
                                    <button className="btn-delete-small" onClick={() => handleRemoveItem(idx)}>Löschen</button>
                                </div>
                                <div className="form-row">
                                    <div className="form-group flex-2">
                                        <label>Projekt</label>
                                        <select 
                                            value={item.project_id} 
                                            onChange={e => handleItemChange(idx, 'project_id', e.target.value)}
                                        >
                                            <option value="">Wähle ein Projekt...</option>
                                            {projects.map(p => (
                                                <option key={p.id} value={p.id}>{p.project_number} - {p.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group flex-2">
                                        <label>Bezeichnung</label>
                                        <input 
                                            type="text" 
                                            placeholder="z.B. Abrechnung KW12" 
                                            value={item.title} 
                                            onChange={e => handleItemChange(idx, 'title', e.target.value)} 
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Projekt-Nr.</label>
                                        <input 
                                            type="text" 
                                            value={item.item_number} 
                                            onChange={e => handleItemChange(idx, 'item_number', e.target.value)} 
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group flex-3">
                                        <label>Notizen / Details</label>
                                        <textarea 
                                            rows="2" 
                                            value={item.notes} 
                                            onChange={e => handleItemChange(idx, 'notes', e.target.value)} 
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group flex-2">
                                        <label>Interner Link (Bautagebuch etc.)</label>
                                        <input 
                                            type="text" 
                                            placeholder="z.B. Bautagebuch Nr 7" 
                                            value={item.internal_link} 
                                            onChange={e => handleItemChange(idx, 'internal_link', e.target.value)} 
                                        />
                                    </div>
                                    <div className="form-group flex-2">
                                        <label>Spezifische Vertretung</label>
                                        <select 
                                            value={item.item_representative_id} 
                                            onChange={e => handleItemChange(idx, 'item_representative_id', e.target.value)}
                                        >
                                            <option value="">Wähle eine Person...</option>
                                            {users.map(u => (
                                                <option key={u.id} value={u.id}>{u.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Ablage / Foto</label>
                                        <button className="btn-upload" onClick={() => alert('Upload kommt bald!')}>📄 Datei hochladen</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="internal-handover-container">
            <div className="internal-handover-header">
                <h2 className="tool-title">🔄 Interne Übergabe</h2>
                <button className="btn-primary" onClick={handleCreateNew}>
                    + Neue Übergabe
                </button>
            </div>

            <div className="internal-handover-content">
                {loading ? (
                    <div className="loading-state">Lädt Übergaben...</div>
                ) : handovers.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">🤝</div>
                        <h3>Noch keine Übergaben</h3>
                        <p>Hier werden zukünftig alle internen Übergaben zwischen Projektleitern und Teams aufgelistet.</p>
                        <button className="btn-secondary" onClick={handleCreateNew}>Erste Übergabe erstellen</button>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="handover-table">
                            <thead>
                                <tr>
                                    <th>Abwesend</th>
                                    <th>Person</th>
                                    <th>Haupt-Vertretung</th>
                                    <th>Status</th>
                                    <th style={{ width: '80px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {handovers.map(h => (
                                    <tr key={h.id}>
                                        <td>
                                            {h.absent_from ? new Date(h.absent_from).toLocaleDateString('de-DE') : '—'} bis {h.absent_until ? new Date(h.absent_until).toLocaleDateString('de-DE') : '—'}
                                        </td>
                                        <td><strong>{h.sender_name}</strong></td>
                                        <td>{h.representative_name || '—'}</td>
                                        <td>
                                            <span className={`status-badge ${h.status}`}>
                                                {h.status === 'draft' ? 'Entwurf' : 'Aktiv'}
                                            </span>
                                        </td>
                                        <td className="actions">
                                            <button className="action-btn" onClick={() => handleEdit(h)} title="Bearbeiten">✏️</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
